import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Code, Palette, Server, Home, Printer, Monitor } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { AspectRatio } from "@/components/ui/aspect-ratio";

// Import project images
import annnyScreenshot from "@/assets/portfolio/annny-screenshot.jpg";
import beladaScreenshot from "@/assets/portfolio/belada-screenshot.jpg";
import keyboardsScreenshot from "@/assets/portfolio/keyboards-screenshot.jpg";
import homeautomationScreenshot from "@/assets/portfolio/homeautomation-screenshot.jpg";
import printingScreenshot from "@/assets/portfolio/printing-screenshot.jpg";
import pcbuildScreenshot from "@/assets/portfolio/pcbuild-screenshot.jpg";

const PortfolioSection = () => {
  const { t } = useTranslation();

  const projects = [
    {
      id: 'annny',
      title: t('portfolio.projects.annny.title'),
      description: t('portfolio.projects.annny.description'),
      category: t('portfolio.projects.annny.category'),
      tags: ['Blazor', 'C#', '.NET', 'Azure', 'SQL Server'],
      link: 'https://annny.com',
      icon: Server,
      image: annnyScreenshot,
      gradient: 'bg-gradient-cyber'
    },
    {
      id: 'belada',
      title: t('portfolio.projects.belada.title'),
      description: t('portfolio.projects.belada.description'),
      category: t('portfolio.projects.belada.category'),
      tags: ['React', 'TypeScript', 'Tailwind', 'Supabase', 'i18n'],
      link: 'https://belada.at',
      icon: Code,
      image: beladaScreenshot,
      gradient: 'bg-gradient-web'
    },
    {
      id: 'keyboards',
      title: t('portfolio.projects.keyboards.title'),
      description: t('portfolio.projects.keyboards.description'),
      category: t('portfolio.projects.keyboards.category'),
      tags: ['QMK', 'PCB Design', 'CAD', 'CNC', 'Soldering'],
      icon: Palette,
      image: keyboardsScreenshot,
      gradient: 'bg-gradient-keyboard'
    },
    {
      id: 'homeautomation',
      title: t('portfolio.projects.homeautomation.title'),
      description: t('portfolio.projects.homeautomation.description'),
      category: t('portfolio.projects.homeautomation.category'),
      tags: ['Home Assistant', 'ESP32', 'MQTT', 'Node-RED', 'Zigbee'],
      icon: Home,
      image: homeautomationScreenshot,
      gradient: 'bg-gradient-cyber'
    },
    {
      id: 'printing',
      title: t('portfolio.projects.printing.title'),
      description: t('portfolio.projects.printing.description'),
      category: t('portfolio.projects.printing.category'),
      tags: ['FDM', 'Resin', 'CAD', 'Fusion 360', 'Cura'],
      icon: Printer,
      image: printingScreenshot,
      gradient: 'bg-gradient-web'
    },
    {
      id: 'pcbuild',
      title: t('portfolio.projects.pcbuild.title'),
      description: t('portfolio.projects.pcbuild.description'),
      category: t('portfolio.projects.pcbuild.category'),
      tags: ['Custom Loops', 'RGB', 'Overclocking', 'Cable Management'],
      icon: Monitor,
      image: pcbuildScreenshot,
      gradient: 'bg-gradient-keyboard'
    }
  ];

  return (
    <section id="portfolio" className="py-20 px-6 relative">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold mb-4 text-glow-cyber">
            <span className="bg-gradient-cyber bg-clip-text text-transparent">
              {t('portfolio.title')}
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {t('portfolio.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <Card 
              key={project.id} 
              className="glass border-glow hover-glow overflow-hidden group transition-all duration-300 hover:scale-[1.02]"
            >
              {/* Project Image */}
              <div className="relative overflow-hidden">
                <AspectRatio ratio={16 / 9}>
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
                </AspectRatio>
                
                {/* Category Badge */}
                <Badge 
                  className="absolute top-3 left-3 bg-primary/90 text-primary-foreground backdrop-blur-sm"
                >
                  {project.category}
                </Badge>
                
                {/* Icon Overlay */}
                <div className="absolute bottom-3 right-3 p-2 rounded-full bg-background/80 backdrop-blur-sm border border-border">
                  <project.icon className="w-5 h-5 text-primary" />
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors">
                  {project.title}
                </h3>
                <p className="text-muted-foreground mb-4 text-sm leading-relaxed line-clamp-3">
                  {project.description}
                </p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tags.slice(0, 4).map((tag, i) => (
                    <Badge 
                      key={i} 
                      variant="secondary" 
                      className="bg-muted/50 text-xs"
                    >
                      {tag}
                    </Badge>
                  ))}
                  {project.tags.length > 4 && (
                    <Badge variant="secondary" className="bg-muted/50 text-xs">
                      +{project.tags.length - 4}
                    </Badge>
                  )}
                </div>
                
                {project.link && (
                  <a href={project.link} target="_blank" rel="noopener noreferrer">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="w-full glass border-cyber-blue text-cyber-blue hover:bg-cyber-blue/10 group/btn"
                    >
                      <ExternalLink className="w-4 h-4 mr-2 transition-transform group-hover/btn:translate-x-1" />
                      {t('portfolio.viewProject')}
                    </Button>
                  </a>
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;
